import * as z from "zod";
import { FlowContentNode, FlowContentNodeSchema } from "../index";

/**
 * Condition type: natural language or rules-based.
 */
export const conditionTypes = ["natural", "rules"] as const;
export type ConditionType = (typeof conditionTypes)[number];

/**
 * Logic operators for rules-based conditions with UI labels.
 */
export const LOGIC_OPERATORS = [
  { value: "==", label: "equals" },
  { value: "!=", label: "not equals" },
  { value: ">", label: "greater than" },
  { value: "<", label: "less than" },
  { value: ">=", label: "greater or equal" },
  { value: "<=", label: "less or equal" },
  { value: "contains", label: "contains" },
  { value: "not_contains", label: "does not contain" },
  { value: "starts_with", label: "starts with" },
  { value: "ends_with", label: "ends with" },
  { value: "is_empty", label: "is empty" },
  { value: "is_not_empty", label: "is not empty" },
] as const;

/**
 * Logic operator values (without labels) for schema validation.
 */
export const logicOperators = LOGIC_OPERATORS.map(
  (op) => op.value,
) as unknown as readonly [
  "==",
  "!=",
  ">",
  "<",
  ">=",
  "<=",
  "contains",
  "not_contains",
  "starts_with",
  "ends_with",
  "is_empty",
  "is_not_empty",
];
export type LogicOperator = (typeof LOGIC_OPERATORS)[number]["value"];

/**
 * Content item for natural language conditions.
 * Supports any FlowContentNode type (text, variables, mentions, etc.)
 */
export const ConditionContentItemSchema: z.ZodType<ConditionContentItem> =
  FlowContentNodeSchema;
export type ConditionContentItem = FlowContentNode;

/**
 * Condition schema - can be natural language or rules-based.
 *
 * For natural conditions: uses `content` array with flow content nodes
 * For rules conditions: uses `variable`, `operator`, `value`
 */
const ConditionSchema_ = z.object({
  type: z.enum(conditionTypes),
  // Natural language condition content (supports text, variables, mentions, etc.)
  content: z.array(FlowContentNodeSchema).optional(),
  // Rules-based condition fields
  variable: z.string().optional(),
  operator: z.enum(logicOperators).optional(),
  value: z.string().optional(),
});

/** Base type from schema inference (no index signature) */
type ConditionBase = z.infer<typeof ConditionSchema_>;

/** Full type with index signature for FlowContentNode compatibility */
export type Condition = ConditionBase & { [key: string]: unknown };

export const ConditionSchema: z.ZodType<Condition> = ConditionSchema_;
